import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { HealthcareAddDonationDialog } from "@project/admin/components/healthcare-add-donation-dialog";
import { HealthcareDonationDetailDialog } from "@project/admin/components/healthcare-donation-detail-dialog";
import type { Field } from "@project/admin/components/resource-table";
import { Button } from "@project/components/ui/button";
import { projectToast } from "@project/lib/toast";
import { generateReceiptPdf } from "@project/lib/receipt";
import { healthcareDonationService } from "@project/services/admin/healthcare-donation.service";
import type { HealthcareDonation } from "@project/types/healthcare-donation";
import { Download, Eye, Plus } from "lucide-react";

const statusOptions = [
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
];

const fields: Field[] = [
  {
    name: "donor_name",
    label: "Donor Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile_number",
    label: "Phone",
    type: "tel",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "category",
    label: "Category / Scheme",
    showInTable: true,
    readOnly: true,
    renderCell: row => {
      const donation = row as HealthcareDonation;
      if (donation.maintenance_schemes) {
        return (
          <span className="font-semibold text-primary">
            {donation.maintenance_schemes}
          </span>
        );
      }
      return donation.category || "—";
    },
  },
  {
    name: "sub_category",
    label: "Sub-Category",
    showInTable: true,
    readOnly: true,
    renderCell: row => {
      const donation = row as HealthcareDonation;
      if (donation.maintenance_schemes) {
        return (
          <span className="text-xs text-muted-foreground">
            Maintenance Scheme
          </span>
        );
      }
      return donation.sub_category || "—";
    },
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "payment_mode",
    label: "Payment Mode",
    showInTable: true,
    readOnly: true,
    renderCell: row => {
      const donation = row as HealthcareDonation;
      if (donation.mode === "online") {
        return "Online";
      }
      return donation.payment_mode ? donation.payment_mode.toUpperCase() : "—";
    },
  },
  {
    name: "mode",
    label: "Mode",
    type: "select",
    options: [
      { value: "online", label: "Online" },
      { value: "offline", label: "Offline" },
    ],
    showInTable: true,
    readOnly: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: statusOptions,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

function downloadReceipt(row: HealthcareDonation) {
  generateReceiptPdf({
    title: "Healthcare Donation Receipt",
    receiptNo: row.receipt_no ?? `TXN-${row.id}`,
    date: String(row.created_at ?? ""),
    donationScheme: row.maintenance_schemes
      ? `Maintenance Scheme: ${row.maintenance_schemes}`
      : `${row.category || "Healthcare"} - ${row.sub_category || ""}`,
    donorName: row.donor_name,
    donorMobile: row.mobile_number,
    donorEmail: row.email ?? "",
    donorAddress: [row.full_address, row.city, row.state, row.pincode]
      .filter(Boolean)
      .join(", "),
    amount: parseFloat(row.amount ?? "0"),
    donationType: row.maintenance_schemes
      ? "Maintenance Scheme"
      : (row.category?.trim() ?? "Healthcare"),
    paymentMode:
      row.mode === "online" ? "Online" : (row.payment_mode ?? "Offline"),
    cashOrChequeNo: row.payment_reference ?? "",
    preferredTithi: "",
    occasion: row.occasion?.trim() ?? "",
  });
}

export default function Page() {
  const [selectedDonation, setSelectedDonation] =
    useState<HealthcareDonation | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleViewDetails = async (row: HealthcareDonation) => {
    setSelectedDonation(null);
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const details = await healthcareDonationService.get(row.id);
      setSelectedDonation(details ?? row);
    } catch {
      setSelectedDonation(row);
      projectToast.error("Could not load full donation details");
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <AdminShell
      title="Healthcare Donations"
      subtitle="Successful and recorded medical/clinic donation records"
    >
      <ApiResourceTable<HealthcareDonation>
        key={listKey}
        title="Donation"
        labelField="donor_name"
        fields={fields}
        searchFields={[
          "donor_name",
          "email",
          "mobile_number",
          "category",
          "sub_category",
          "maintenance_schemes",
          "display_name",
        ]}
        defaultValues={{}}
        statusFilterOptions={statusOptions}
        initialStatusFilter="success"
        csvFileName="healthcare-donations"
        toolbarActions={
          <Button
            type="button"
            size="sm"
            className="flex-1 gap-1 rounded-xl font-semibold sm:flex-none"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Donation
          </Button>
        }
        list={async query => {
          const result = await healthcareDonationService.listPage(query);
          return {
            ...result,
            data: result.data || [],
          };
        }}
        renderRowActions={row => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="View Details"
              onClick={() => void handleViewDetails(row)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              title="Download Receipt"
              onClick={() => downloadReceipt(row)}
            >
              <Download className="h-4 w-4" />
              <span className="text-xs">Receipt</span>
            </Button>
          </div>
        )}
      />

      <HealthcareAddDonationDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSuccess={() => setListKey(key => key + 1)}
      />

      <HealthcareDonationDetailDialog
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        donation={selectedDonation}
        loading={loadingDetails}
        onDownloadReceipt={downloadReceipt}
      />
    </AdminShell>
  );
}
